




























import { Component, Vue, Prop, Watch, Emit, PropSync } from 'vue-property-decorator';

@Component
export default class NumberInput extends Vue {
  @PropSync("value", { type: Number, default: 0 })
  syncValue!: number;

  @Prop({ type: String, default: 'Zahl eingeben' })
  label!: string;

  @Prop({ type: Number, default: Number.MIN_SAFE_INTEGER })
  min!: number;

  @Prop({ type: Number, default: Number.MAX_SAFE_INTEGER })
  max!: number;

  @Prop({ type: Number, default: 1 })
  step!: number;

  @Prop({ type: String, default: 'mdi-minus' })
  minusIcon!: string;

  @Prop({ type: String, default: 'mdi-plus' })
  plusIcon!: string;

  @Prop({ type: Boolean, default: true })
  outlined!: boolean;

  @Prop({ type: Boolean, default: false })
  dense!: boolean;

  @Prop({ type: Boolean, default: false })
  disabled!: boolean;

  @Prop({ type: Boolean, default: false })
  readonly!: boolean;

  @Prop({ type: String, default: '' })
  placeholder!: string;

  @Prop({ type: Boolean, default: true })
  persistentHint!: boolean;

  @Prop({ type: Boolean, default: true })
  showValidation!: boolean;

  @Prop({ type: Array, default: () => [] })
  customRules!: Array<(v: any) => boolean | string>;

  private internalValue: number = 0;

  mounted() {
    this.validateAndSetValue(this.syncValue);
  }

  @Watch('syncValue')
  onSyncValueChange(newVal: number) {
    this.validateAndSetValue(newVal);
  }

  @Watch('min')
  onMinChange(newVal: number) {
    this.validateAndSetValue(this.syncValue);
  }

  @Watch('max')
  onMaxChange(newVal: number) {
    this.validateAndSetValue(this.syncValue);
  }

  get validationRules() {
    if (!this.showValidation) return [];

    const rules = [
      (v: any) => v !== null && v !== undefined && v !== '' || 'Bitte geben Sie eine Zahl ein',
      (v: number) => v >= this.min || `Minimum ist ${this.min}`,
      (v: number) => v <= this.max || `Maximum ist ${this.max}`
    ];

    return [...rules, ...this.customRules];
  }

  get hint() {
    return `Werte zwischen ${this.min} - ${this.max}`
  }

  increment() {
    if (this.disabled || this.readonly) return;

    const newValue = this.internalValue + this.step;
    // Adjust to nearest step
    if (newValue % this.step !== 0) {
      const steps = Math.round(newValue / this.step);
      this.validateAndSetValue(steps * this.step);
      return;
    }
    this.validateAndSetValue(newValue);
  }

  decrement() {
    if (this.disabled || this.readonly) return;

    const newValue = this.internalValue - this.step;
    // Adjust to nearest step
    if (newValue % this.step !== 0) {
      const steps = Math.round(newValue / this.step);
      this.validateAndSetValue(steps * this.step);
      return;
    }
    this.validateAndSetValue(newValue);
  }

  onInput(value: number | string): void {
    this.validateAndSetValue(value);
  }

  private async validateAndSetValue(value: number | string): Promise<void> {
    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (numValue === null || numValue === undefined || isNaN(numValue)) {
      this.internalValue = this.min >= 0 ? this.min : 0;
      return;
    }

    this.internalValue = numValue;
    await this.$globalHelper.delay(5);

    if (numValue < this.min) {
      this.internalValue =  this.min;
    } else if (numValue > this.max) {
      this.internalValue =  this.max;
    }
    this.syncValue = this.internalValue;
    await this.$globalHelper.delay(5);
  }

  // Public methods
  public setValue(value: number | string): void {
    this.validateAndSetValue(value);
  }

  public getValue(): number {
    return this.internalValue;
  }

  public reset(): void {
    this.validateAndSetValue(this.min);
  }

  public incrementBy(amount: number): void {
    const newValue = this.internalValue + amount;
    this.validateAndSetValue(newValue);
  }

  public decrementBy(amount: number): void {
    const newValue = this.internalValue - amount;
    this.validateAndSetValue(newValue);
  }
}
